import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/system";
import ConversionsUploadFilter from "./ConversionsUploadFilter";
import ConversionsUploadTable from "./ConversionsUploadTable";
import { useBizDevelopmentDashboardMobile } from "../../../../../../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboardMobile";
import {
  selectBizMobileBeAwareConversionsUploadLoading,
  selectBizMobileBeAwareConversionsUploadOutput,
} from "../../../../../../../../../features/bizDevelopmentDashboardMobileSlice";
import { exportBeAwareTableToCSV } from "../../../../../../DashboardDesktop/BeAware/beAwareCsvHelpers";

// CSV column order/titles — identical to Conversions Upload Desktop.
const tableHeadings = {
  upload_date: "Upload Date",
  loaded_ftd: "Loaded FTD",
  loaded_ftd_of_total_exc_incent: "% Loaded FTD of Total FTD except Incent",
  total_ftd: "Total FTD",
  loaded_qftd: "Loaded QFTD",
  total_qftd: "Total QFTD",
  brand_total_ftd: "Brand Total FTD",
  players_total_ftd: "Players Total FTD",
  players_total_ftd_without_incent: "Players Total FTD Without Incent",
  incent_ftd: "Incent FTD",
  lack_of_player_report: "Lack of Player Report",
  direct_ftd: "Direct FTD",
  gbraid_or_wbraid: "Gbraid or Wbraid",
  paid_no_click_id: "Paid no Click ID",
  problematic_ftd: "Problematic FTD",
  multiple_ftds: "Multiple FTDs",
  eligible_with_exact_click_id: "Eligible with Exact Click ID",
  total_loaded_success: "Total Loaded Success",
  loaded_success_to_google: "Loaded Success to Google",
  loaded_success_to_bing: "Loaded Success to Bing",
  abs_late_ftd_succeeded: "Abs late FTD Succeeded",
  total_loaded_failure: "Total Loaded Failure",
  percent_late_ftd: "Percent Late FTD",
  this_click_is_too_old: "This Click is Too Old",
};

// Keep raw values; empty string when missing so the CSV shows a blank cell.
const raw = (value: any) =>
  value === null || value === undefined ? "" : String(value);

// API date (e.g. "2026-07-11") → "DD-MM-YYYY", or "" when invalid.
const formatDate = (value: any) => {
  if (!value) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : String(value);
};

// (numerator / denominator) * 100 as a number, or null.
const percentValue = (numerator: any, denominator: any) => {
  const n = Number(numerator);
  const d = Number(denominator);
  if (!d || Number.isNaN(n) || Number.isNaN(d)) return null;
  return (n / d) * 100;
};

// Converts an API fraction (e.g. 0.857...) to a percentage number, or null.
const fractionToPercent = (value: any) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return null;
  return n * 100;
};

// Converts an API fraction to a rounded percentage label (e.g. "85%"), or "".
const fractionToPercentLabel = (value: any) => {
  const percent = fractionToPercent(value);
  return percent === null ? "" : `${Math.round(percent)}%`;
};

// CSV cell resolver: mirror the merged/badge columns as text (matches Desktop).
const getConversionsCellValue = (key: string, row: any) => {
  if (key === "loaded_ftd" || key === "loaded_qftd") {
    const count = row[key];
    const percent =
      key === "loaded_ftd" ? row.loaded_ftd_of_total : row.loaded_qftd_of_total;
    if (count === "" || count === null || count === undefined) return "";
    return percent === null || percent === undefined
      ? String(count)
      : `${count} (${Math.round(percent)}%)`;
  }
  if (key === "loaded_ftd_of_total_exc_incent") {
    const percent = row[key];
    return percent === null || percent === undefined
      ? ""
      : `${Math.round(percent)}%`;
  }
  return row[key] ?? "";
};

const ConversionsUpload = () => {
  const [searchParams] = useSearchParams();
  const workspaceParam = searchParams.get("workspace");

  const isLoading = useSelector(selectBizMobileBeAwareConversionsUploadLoading);
  const output = useSelector(selectBizMobileBeAwareConversionsUploadOutput);

  const { fetchBeAwareConversionsUploadData, getSiteFromWorkspace } =
    useBizDevelopmentDashboardMobile();

  useEffect(() => {
    const sites = getSiteFromWorkspace();
    if (sites.length === 0) return;
    fetchBeAwareConversionsUploadData(sites);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceParam]);

  // CSV rows — mapped from the same API keys as Conversions Upload Desktop.
  const csvRows = useMemo(
    () =>
      (output ?? []).map((item: Record<string, any>) => ({
        upload_date: formatDate(item.brand_action_date),
        loaded_ftd: raw(item.FTD_loaded),
        loaded_ftd_of_total: percentValue(
          item.FTD_loaded,
          item.brand_total_ftd,
        ),
        loaded_ftd_of_total_exc_incent: fractionToPercent(
          item.loaded_from_total_exc_incent,
        ),
        total_ftd: raw(item.brand_total_ftd),
        loaded_qftd: raw(item.QFTD_loaded),
        loaded_qftd_of_total: percentValue(item.QFTD_loaded, item.Total_Qftd),
        total_qftd: raw(item.Total_Qftd),
        brand_total_ftd: raw(item.brand_total_ftd),
        players_total_ftd: raw(item.players_total_ftd),
        players_total_ftd_without_incent: raw(
          item.players_total_ftd_without_incent,
        ),
        incent_ftd: raw(item.incent_ftd),
        lack_of_player_report: raw(item.lack_of_player_report),
        direct_ftd: raw(item.direct_ftd),
        gbraid_or_wbraid: raw(item.gbraid_or_wbraid),
        paid_no_click_id: raw(item.Paid_no_Click_Id),
        problematic_ftd: raw(item.problematic_ftd),
        multiple_ftds: raw(item.multiple_ftds),
        eligible_with_exact_click_id: raw(item.eligible_with_exact_click_id),
        total_loaded_success: raw(item.total_loaded_success),
        loaded_success_to_google: raw(item.loaded_success_to_google),
        loaded_success_to_bing: raw(item.loaded_success_to_bing),
        abs_late_ftd_succeeded: raw(item.Abs_late_ftd_succeeded),
        total_loaded_failure: raw(item.total_loaded_failure),
        percent_late_ftd: fractionToPercentLabel(item.Percent_Late_ftd),
        this_click_is_too_old: raw(item.This_click_is_too_old),
      })),
    [output],
  );

  const handleDownload = () => {
    exportBeAwareTableToCSV({
      tableHeadings,
      rows: csvRows,
      filename: "conversions_upload",
      getCellValue: getConversionsCellValue,
    });
  };

  return (
    <>
      <Box sx={{ px: 2 }}>
        <ConversionsUploadFilter
          onDownload={handleDownload}
          downloadDisabled={isLoading || csvRows.length === 0}
        />
        <ConversionsUploadTable data={output ?? []} isLoading={isLoading} />
      </Box>
    </>
  );
};

export default ConversionsUpload;
